import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  createPage,
  fetchPageById,
  fetchPagesList,
  publishPage,
  unpublishPage,
  updatePageById,
} from './thunks';
import type { PageSliceInitialState, PagesState } from './types';

export const initialState: PagesState & PageSliceInitialState = {
  list: [],
  loading: 'idle',
  error: null,
  currentPageId: '',
  currentPage: null,
  createPageLoading: false,
  readPageLoading: false,
  updatePageLoading: false,
  publishing: 'idle',
  pageNotFound: false,
  pageInvalid: false,
  consecutiveSaveFailures: 0,
  selectedPath: '',
  unpublishing: false,
};

const pagesSlice = createSlice({
  name: 'pages',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearPagesList: (state) => {
      state.list = [];
      state.loading = 'idle';
      state.error = null;
    },
    resetPublishing: (state) => {
      if (state.publishing === 'published') {
        state.publishing = 'idle';
      }
    },
    setSelectedPath(state, action: PayloadAction<string | null>) {
      state.selectedPath = action.payload;
    },
  },
  extraReducers: (builder) => {
    // --- Create Page ---
    builder
      .addCase(createPage.pending, (state) => {
        state.createPageLoading = true;
      })
      .addCase(createPage.fulfilled, (state, action) => {
        state.createPageLoading = false;
        state.currentPageId = action.payload.id;
      })
      .addCase(createPage.rejected, (state) => {
        state.createPageLoading = false;
      });
    // --- Fetch Page by ID ---
    builder
      .addCase(fetchPageById.pending, (state) => {
        state.readPageLoading = true;
        state.pageNotFound = false;
        state.pageInvalid = false;
      })
      .addCase(fetchPageById.fulfilled, (state, action) => {
        state.readPageLoading = false;
        state.currentPage = action.payload;
        state.currentPageId = action.payload.id;
        state.pageNotFound = false;
        state.pageInvalid = false;
        state.selectedPath = action.payload.path ?? '';
      })
      .addCase(fetchPageById.rejected, (state, action) => {
        state.readPageLoading = false;
        if (action.payload === 'PAGE_NOT_FOUND') {
          state.pageNotFound = true;
        }
        if (action.payload === 'PAGE_INVALID') {
          state.pageInvalid = true;
        }
      });
    // --- Update Page by ID ---
    builder
      .addCase(updatePageById.pending, (state) => {
        state.updatePageLoading = true;
      })
      .addCase(updatePageById.fulfilled, (state, action) => {
        state.updatePageLoading = false;
        state.currentPage = action.payload;
        state.currentPageId = action.payload.id;
        state.consecutiveSaveFailures = 0; // Reset on success
      })
      .addCase(updatePageById.rejected, (state) => {
        state.updatePageLoading = false;
        state.consecutiveSaveFailures += 1;
      });
    // --- Fetch Pages List ---
    builder
      .addCase(fetchPagesList.pending, (state) => {
        state.loading = 'loading';
        state.error = null;
      })
      .addCase(fetchPagesList.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        state.list = action.payload;
        state.error = null;
      })
      .addCase(fetchPagesList.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.error.message || 'Failed to fetch pages';
      });
    // --- Publish page ---
    builder
      .addCase(publishPage.pending, (state) => {
        state.publishing = 'publishing';
      })
      .addCase(publishPage.fulfilled, (state, action) => {
        state.publishing = 'published';
        state.currentPage = action.payload.page;
        state.currentPageId = action.payload.page.id;
      })
      .addCase(publishPage.rejected, (state) => {
        state.publishing = 'idle';
      });
    // --- Unpublish page ---
    builder
      .addCase(unpublishPage.pending, (state) => {
        state.unpublishing = true;
      })
      .addCase(unpublishPage.fulfilled, (state, action) => {
        state.unpublishing = false;
        state.currentPage = action.payload;
      })
      .addCase(unpublishPage.rejected, (state) => {
        state.unpublishing = false;
      });
  },
});

export const { clearError, clearPagesList, resetPublishing, setSelectedPath } = pagesSlice.actions;
export default pagesSlice.reducer;
